import { cn } from '../../../lib/styles';
import { Icon } from '../icon/icon.spark';
import type { IconSize } from '../icon/types';
import { Spinner } from '../spinner/spinner.spark';
import type { ButtonProps, ButtonSize } from './types';

const BUTTON_ICON_SIZE: Record<ButtonSize, IconSize> = {
  xs: 'xs',
  sm: 'sm',
};

const SIZE_CONFIG: Record<ButtonSize, { base: string; iconOnly: string; collapseExpand: string }> =
  {
    xs: {
      base: 'h-6 px-2 gap-x-0.5 text-xs rounded-md',
      iconOnly: 'w-6 px-0',
      collapseExpand: 'sm:w-fit sm:px-1.5',
    },
    sm: {
      base: 'h-spark-component px-3 gap-x-1 text-sm rounded-lg',
      iconOnly: 'w-spark-component px-0',
      collapseExpand: 'sm:w-fit sm:px-3',
    },
  };

export function Button(_props: ButtonProps) {
  const { variant = 'primary', size = 'sm', type = 'button', ...props } = _props;

  const isDisabled = props.disabled || props.loading;

  const showIcon = props.icon && !props.loading;
  const hasGraphic = props.icon || props.loading; // Showing icon or spinner
  const isIconOnly = hasGraphic && !props.children;

  const shouldCollapse = hasGraphic && props.children && props.collapsible;

  const sizeConfig = SIZE_CONFIG[size];

  return (
    <button
      formNoValidate
      type={type}
      onClick={props.onClick}
      disabled={isDisabled}
      aria-busy={props.loading}
      aria-label={props.ariaLabel}
      aria-expanded={props.ariaExpanded}
      aria-controls={props.ariaControls}
      aria-current={props.ariaCurrent}
      className={cn(
        'flex w-fit shrink-0 items-center justify-center whitespace-nowrap border border-white/15 text-spark-text-primary transition focus:outline-hidden',
        sizeConfig.base,
        (isIconOnly || shouldCollapse) && sizeConfig.iconOnly,
        shouldCollapse && sizeConfig.collapseExpand,
        props.className,
        isDisabled
          ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-spark-text-secondary'
          : 'cursor-pointer shadow-xs focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',
        !isDisabled && {
          'bg-spark-purple text-spark-text-inverse hover:brightness-125 focus-visible:ring-spark-purple':
            variant === 'primary',
          'border-gray-300 bg-white hover:bg-gray-100 focus-visible:ring-spark-purple':
            variant === 'secondary',
          'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-600': variant === 'danger',
          'border-gray-300 bg-white hover:border-red-600 hover:bg-red-50 hover:text-red-700 focus-visible:ring-red-600':
            variant === 'dangerOutline',
          'border-none shadow-none hover:border-spark-text-danger hover:text-spark-text-danger focus-visible:ring-spark-text-danger':
            variant === 'dangerBare',
          'border-none shadow-none hover:bg-gray-100 focus-visible:ring-spark-purple':
            variant === 'bare',
        },
      )}
    >
      {props.loading && <Spinner />}
      {showIcon && (
        <span aria-hidden>
          {/** biome-ignore lint/style/noNonNullAssertion: we have an icon if showIcon is true */}
          <Icon name={props.icon!} size={BUTTON_ICON_SIZE[size]} />
        </span>
      )}
      {props.children && (
        // If we have a graphic, hide the text on small screens
        <span className={shouldCollapse ? 'hidden sm:block' : undefined}>{props.children}</span>
      )}
    </button>
  );
}
